import type { MobileTabs } from "./App";
import { PaneFooterAds } from "./PaneFooterAds";
import { DefaultErrorBoundary } from "./components/DefaultErrorBoundary/DefaultErrorBoundary";
import { SearchScreen } from "./domain/SearchScreen/SearchScreen";
import { UserPreferencesScreen } from "./domain/UserPreferencesScreen/UserPreferencesScreen";
import { useIsTripScreen } from "./utils/hooks/useIsTripScreen";
import { type LayoutType } from "./utils/hooks/useLayout";
import {
  type ReturnStage,
  useTypedLocation,
} from "./utils/hooks/useTypedLocation";
import { getScreenKey } from "./domain/TripPlanner/util/getScreenKey";
import { ScrollableContainer } from "./components/ScrollableContainer/ScrollableContainer";
import { useScreenMinWidth } from "./utils/hooks/useScreenMinWidth";
import { tw } from "./design-system/tw";

const leftPaneScrollContainerId = "paneScrollContainer";

type Props = {
  highlightedTab: MobileTabs;
  layout: LayoutType;
  sectionTitle?: string;
  openScheduleIndex?: number;
  returnsFlowLocation?: ReturnStage;
};

export function PaneContent({
  highlightedTab,
  layout,
  sectionTitle,
  returnsFlowLocation,
  openScheduleIndex,
}: Props) {
  const location = useTypedLocation();
  const isTripsScreen = useIsTripScreen();
  const { isSmScreen } = useScreenMinWidth();

  const onFullScreenTripPage =
    openScheduleIndex !== undefined && layout === "mobile";

  const screenKey = getScreenKey(location.hash);
  const paneStackOpen = screenKey !== undefined && layout !== "mobile";

  /* The ad is only shown on the search tab and whilst live schedules aren't loading on the segment pane.
  Since we don't want to rerequest it when toggling between tabs, we hide it on tabs that aren't the
  search one instead of unmounting it */
  const shouldHideAds =
    (highlightedTab !== "search" && highlightedTab !== "trips") ||
    (highlightedTab === "trips" && !isSmScreen) || // Hide ads on trips tab on mobile
    onFullScreenTripPage ||
    paneStackOpen;

  return (
    <DefaultErrorBoundary defaultMessage>
      <ScrollableContainer zIndex={1} logScrollPercent>
        <div
          className={tw(
            "relative min-h-screen pb-6",
            isTripsScreen && "[overflow-anchor:none]",
            "md:min-h-0 md:w-full md:flex-grow-1 md:pb-0",
          )}
          data-testid="paneScrollContainer"
          aria-labelledby="pane-label"
          id={leftPaneScrollContainerId}
        >
          {sectionTitle && (
            <h2 id="pane-label" className="sr-only">
              {sectionTitle}
            </h2>
          )}
          <div
            className={tw(
              "inline bg-fill-primary",
              highlightedTab === "preferences" && "bg-fill-primary-hover",
            )}
          >
            {highlightedTab === "search" ||
            highlightedTab === "hotels" ||
            highlightedTab === "trips" ? (
              <SearchScreen
                context="transport"
                openScheduleIndex={openScheduleIndex}
                returnsFlowLocation={returnsFlowLocation}
              />
            ) : (
              <UserPreferencesScreen />
            )}
          </div>
          <PaneFooterAds hide={shouldHideAds} />
        </div>
      </ScrollableContainer>
    </DefaultErrorBoundary>
  );
}
